import React, { useState, useContext, useMemo, useEffect } from 'react';
import Cookies from 'universal-cookie';
import { Link } from 'react-router-dom/cjs/react-router-dom.min';
import { ConfirmDialog } from 'primereact/confirmdialog';

import constants from 'constants/index';
import TextSnippetWrapper from 'components/TextSnippetWrapper';
import GlobalTooltip from 'components/GlobalTooltip';
import ImageComponent from 'shared/ImageComponent';
import { useToast } from 'context/ToastContext';
import { toastConstant } from 'constants/toastmessage';
import { EditPaymentRecordV2 } from './components/EditPaymentRecordV2/EditPaymentRecordV2';
import { dollarFormat } from 'utils/utils';
import { useDeletePaymentRecordQuery } from './hooks/mutations/useDeletePaymentRecordQuery';
import { RefundPaymentV2 } from './components/RefundPaymentV2/RefundPaymentV2';
import { UserDetailsContext } from 'context/userDetailsContext';
import { handlePageNumberInPagination } from 'common/pagination';
import { handleDateTimeOffset } from 'utils/utility_functions/timezone';
import ResizableDataTable from 'components/ResizableDataTable/ResizableDataTable';
import EllipsisPagination from 'components/common/EllipsisPagination/EllipsisPagination';
import PaymentHistoryFiltersV2 from './components/PaymentHistoryFiltersV2/PaymentHistoryFiltersV2';
import { usePaymentHistoryV2Query } from './hooks/queries/usePaymentHistoryV2Query';
import { getPageLimit, pickExtractedDate, getFilePathForPagination } from './helpers/paymentHistoryHelpersV2';

const cookie = new Cookies();

export const PaymentHistoryV2 = (props) => {
  const {
    isFromCaseSpecificModule,
    handlePaymentHistoryRedirection,
    isFromGlobalBilling,
    caseView,
    caseDetails,
    isLeadSpecific,
    getCaseDetails,
    isActiveTab = true,
  } = props || {};
  const [selectedRecord, setSelectedRecord] = useState();
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [searchKey, setSearchKey] = useState('');
  const [dateFilter, setDateFilter] = useState('');
  const [showRecordModal, setShowRecordModal] = useState(false);
  const [showRefundModal, setShowRefundModal] = useState(false);
  const [selectedPractice, setSelectedPractice] = useState(null);
  const [pageLimit, setPageLimit] = useState(getPageLimit(isLeadSpecific, isFromCaseSpecificModule, useContext(UserDetailsContext)) ?? 10);
  const [currentPage, setCurrentPage] = useState(1);
  const [sortField, setSortField] = useState(null); // Default sort by created_at
  const [sortOrder, setSortOrder] = useState(null); // -1 for desc, 1 for asc

  const userContext = useContext(UserDetailsContext);
  const { addToast } = useToast();
  let tenantId = cookie.get('tenantId');

  // Map DataTable field names to API sort_by field names
  // API supports: created_at, payment_record_date, payment_record_amount, paid_amount, case_name, billing_contact_name,
  // payment_method, payment_ref_number, payment_record_number, description, debit_account_name, deposit_account_name,
  // created_by_name, due_amount, payment_record_status
  const getCurrentSortField = useMemo(() => {
    if (!sortField) return '';
    const fieldMap = {
      // Date column
      date: 'payment_record_date',
      payment_record_date: 'payment_record_date',
      // Amount columns
      amount: 'payment_record_amount',
      payment_record_amount: 'payment_record_amount',
      paid_amount: 'paid_amount',
      due_amount: 'due_amount',
      // Case column
      case: 'case_name',
      case_name: 'case_name',
      // Payment method column
      payment_method: 'payment_method',
      // Related To column
      related_to: 'payment_ref_number',
      payment_ref_number: 'payment_ref_number',
      payment_record_number: 'payment_record_number',
      // Notes column
      notes: 'description',
      description: 'description',
      // Paid From column
      debit_account_name: 'debit_account_name',
      // Paid To column
      deposit_account_name: 'deposit_account_name',
      // Entered By column
      entered_by: 'created_by_name',
      created_by_name: 'created_by_name',
      // Billing contact (not shown in table but sortable)
      billing_contact_name: 'billing_contact_name',
      // Status (not shown in table but sortable)
      payment_record_status: 'payment_record_status',
      // Created at (default sort)
      created_at: 'created_at',
    };
    return fieldMap[sortField] || '';
  }, [sortField]);

  const getCurrentSortOrder = useMemo(() => {
    if (sortOrder === null || sortOrder === undefined) return ''; // Return empty string when no sort order
    return sortOrder === 1 ? 'asc' : 'desc';
  }, [sortOrder]);

  // Compute query filters from filter state
  const queryFilters = useMemo(() => {
    const dateFilters = pickExtractedDate(dateFilter || [], userContext?.userDetails?.timezone);
    return {
      case_id: caseView ? caseDetails?.case_id : '',
      search_key: searchKey,
      practice_area: selectedPractice,
      payment_date_lower: dateFilters.payment_date_lower || '',
      payment_date_upper: dateFilters.payment_date_upper || '',
      sort_by: getCurrentSortField,
      sort_order: getCurrentSortOrder,
    };
  }, [
    dateFilter,
    searchKey,
    selectedPractice,
    caseView,
    caseDetails,
    userContext?.userDetails?.timezone,
    getCurrentSortField,
    getCurrentSortOrder,
  ]);

  // React Query for payment history list
  const { paymentRecords, totalPages, totalCount, isLoading, refetch } = usePaymentHistoryV2Query({
    limit: pageLimit,
    page: currentPage,
    ...queryFilters,
    enabled: isActiveTab, // Only fetch when tab is active
  });

  const totalRecords = totalCount || 0;

  // Delete payment record mutation
  const deletePaymentRecord = useDeletePaymentRecordQuery({
    onSuccess: () => {
      addToast(false, toastConstant.toasterType.SUCCESS, toastConstant.api.SUCCESS, toastConstant.message.DELETE_PAYMENT_RECORD_SUCCESS);
      refetch();
      if (typeof getCaseDetails === 'function') {
        getCaseDetails();
      }
    },
    onError: (err) => {
      let msg = toastConstant.message.DELETE_PAYMENT_RECORD_FAILED;
      if (err && err?.response?.data?.message) {
        msg = err.response.data.message;
      }
      addToast(false, toastConstant.toasterType.ERROR, toastConstant.api.FAILED, msg);
    },
  });

  // Handle page change
  const handlePageChange = (page) => {
    setCurrentPage(page);
    const filePath = getFilePathForPagination(isLeadSpecific, isFromCaseSpecificModule);
    handlePageNumberInPagination(userContext, filePath.module, filePath.subModule, filePath.listName, page);
  };

  // Handle page size change
  const handlePageSizeChange = (newPageSize) => {
    setPageLimit(newPageSize);
    setCurrentPage(1); // Reset to first page when page size changes
    const filePath = getFilePathForPagination(isLeadSpecific, isFromCaseSpecificModule);
    handlePageNumberInPagination(userContext, filePath.module, filePath.subModule, filePath.listName, newPageSize);
  };

  // Handle filter changes - reset to page 1
  useEffect(() => {
    setCurrentPage(1);
  }, [searchKey, dateFilter, selectedPractice, sortField, sortOrder]);

  // Sorting handler
  const onSort = (event) => {
    const { sortField: field, sortOrder: order } = event;

    setSortField(field);
    setSortOrder(order);
    setCurrentPage(1); // Reset to first page when sorting changes
  };

  const onCreateSuccess = () => {
    setShowRecordModal(false);
    setShowRefundModal(false);
    refetch();
    if (typeof getCaseDetails === 'function') {
      getCaseDetails();
    }
  };

  const notesBody = (rowData) => {
    return (
      <React.Fragment>
        <span className="p-column-title text-break">Notes</span>
        <span className="text-break" title={rowData.description}>
          {rowData.description}
        </span>
      </React.Fragment>
    );
  };

  const paidFromBody = (rowData) => {
    return (
      <React.Fragment>
        <span className="p-column-title text-break">Paid From</span>
        <span className="text-break" title={rowData?.debit_account_name || ''}>
          {rowData?.debit_account_name || '-'}
        </span>
      </React.Fragment>
    );
  };

  const paidToBody = (rowData) => {
    return (
      <React.Fragment>
        <span className="p-column-title text-break">Paid To</span>
        <span className="text-break" title={rowData?.deposit_account_name || ''}>
          {rowData?.deposit_account_name || '-'}
        </span>
      </React.Fragment>
    );
  };

  const paymentMethodBody = (rowData) => {
    return (
      <React.Fragment>
        <span className="p-column-title text-break">Payment method</span>
        <span className="text-break" title={rowData.payment_method}>
          {rowData.payment_method}
        </span>
      </React.Fragment>
    );
  };

  const amountBody = (rowData) => {
    return (
      <React.Fragment>
        <span className="p-column-title text-break">Amount</span>
        <span className="text-break" title={dollarFormat(rowData?.payment_record_amount)}>
          {dollarFormat(rowData?.payment_record_amount)}
        </span>
      </React.Fragment>
    );
  };

  const relatedToBody = (rowData) => {
    return (
      <React.Fragment>
        <span className="p-column-title text-break">Related To</span>
        <span className="text-break" title={rowData?.payment_ref_number}>
          {rowData?.payment_ref_number}
        </span>
      </React.Fragment>
    );
  };

  const enterByBody = (rowData) => {
    // V3 API uses firm_user_id, but support both for backward compatibility
    const firmUserId = rowData?.created_by_details?.firm_user_id || rowData?.created_by_details?.user_id;
    let name = rowData?.created_by_details?.first_name;
    if (rowData?.created_by_details?.last_name) name = name + ' ' + rowData?.created_by_details?.last_name;

    return (
      <>
        <span className="p-column-title text-break">Entered By</span>
        <Link
          to={firmUserId ? `/settings/team-management/${firmUserId}` : '/settings/team-management'}
          className="redirection-link-no-text-transform"
        >
          <div className="d-flex align-items-center">
            <ImageComponent
              filePath={`${tenantId}/${firmUserId}/profile_image`}
              fileName={rowData?.created_by_details?.first_name}
              fileSize="small.jpg"
            />
            <span
              className="redirection-link-no-text-transform"
              style={{
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                width: '150px',
              }}
              title={name}
            >
              {firmUserId && <i className="fas fa-user-tie me-2"></i>}
              {name}
            </span>
          </div>
        </Link>
      </>
    );
  };

  const dateBody = (rowData) => {
    return (
      <React.Fragment>
        <span className="p-column-title text-break">Date</span>
        <span
          className="text-break"
          title={handleDateTimeOffset(userContext?.userDetails?.timezone, rowData.payment_record_date, constants.month_date_year_format)}
        >
          {handleDateTimeOffset(userContext?.userDetails?.timezone, rowData.payment_record_date, constants.month_date_year_format)}
        </span>
      </React.Fragment>
    );
  };

  const caseBody = (rowData, ...rest) => {
    const { case_id, is_lead } = rowData;
    const mainModuleName = is_lead ? '/leads' : '/cases';
    let route = case_id ? `${mainModuleName}/${case_id}` : mainModuleName;

    return (
      <React.Fragment>
        <span className="p-column-title text-break">Case</span>
        <Link to={route} className="redirection-link-no-text-transform" title={rowData.case_name ? rowData.case_name : undefined}>
          {rowData.case_name ? rowData.case_name : '-'}
        </Link>
      </React.Fragment>
    );
  };

  const actionBody = (rowData) => {
    return (
      <div>
        <span className="p-column-title text-break">Action</span>

        {!rowData.is_refund &&
          (!rowData?.fund_transfer_record ? rowData.payment_record_ref === '' : true) &&
          rowData.payment_record_type !== 'REFUND' &&
          rowData?.is_forwarded !== true &&
          !rowData?.is_transferred &&
          rowData?.payment_ref_number?.trim() !== 'TEMPORARY FUNDS' && (
            <GlobalTooltip
              className="ms-2"
              tooltip={rowData?.qb_charge_id ? "Payment made through payment gateway(client portal) can't be edited." : 'Edit'}
            >
              <span role="button">
                <i
                  className={`icon-edit ${
                    (rowData?.qb_charge_id ||
                      parseFloat(rowData?.due_amount) <= 0 ||
                      rowData?.payment_ref_number?.trim() === 'TEMPORARY FUNDS') &&
                    'disabled-icon'
                  }`}
                  onClick={(e) => {
                    setSelectedRecord({ ...rowData });
                    setShowRecordModal(true);
                  }}
                  id="edit"
                ></i>
              </span>
            </GlobalTooltip>
          )}

        {(!rowData.is_refund || (rowData.is_refund === true && rowData.payment_record_type === 'REFUND')) && !rowData?.is_transferred && (
          <GlobalTooltip className="ms-2" tooltip={'Delete'}>
            <span role="button">
              <i
                className={`icon-delete`}
                onClick={(e) => {
                  setSelectedRecord({ ...rowData });
                  setConfirmDelete(true);
                }}
                id="delete"
              ></i>
            </span>
          </GlobalTooltip>
        )}
        {rowData.is_refund === false &&
          rowData.payment_record_ref === '' &&
          rowData.payment_record_type !== 'REFUND' &&
          !rowData?.fund_transfer_record &&
          rowData?.payment_ref_number?.trim() !== 'TEMPORARY FUNDS' && (
            <span
              className={
                'ms-2 redirection-link' +
                (rowData?.qb_charge_id && !userContext?.userDetails?.is_quickbook_connected ? 'pe-none opacity-50 disabled-icon' : '')
              }
              role="button"
              onClick={() => {
                setSelectedRecord({ ...rowData });
                setShowRefundModal(true);
              }}
              style={{
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              Refund
            </span>
          )}
      </div>
    );
  };

  const deleteRequest = () => {
    if (!selectedRecord?.sk) return;

    deletePaymentRecord.mutate({
      payment_record_sk: selectedRecord.sk,
      is_lead: isLeadSpecific ? isLeadSpecific : selectedRecord?.is_lead,
    });
    setConfirmDelete(false);
  };

  const onSearchKeyDown = (e) => {
    let code = e.keyCode ? e.keyCode : e.which;
    if (code === 13) {
      const searchValue = e.target.value;
      setSearchKey(searchValue);
      setCurrentPage(1);
    }
  };

  const onChangeSearch = (e) => {
    let value = e.target.value;
    setSearchKey(value);
    if (!value) {
      setCurrentPage(1);
    }
  };

  const onChangeCalendar = (e) => {
    setDateFilter(e.value);
    setCurrentPage(1);
  };

  const onChangePractice = (e) => {
    const newPracticeValue = [...e.value];
    setSelectedPractice(newPracticeValue);
    setCurrentPage(1);
  };

  const refreshData = () => {
    setSearchKey('');
    setDateFilter('');
    setSelectedPractice(null);
    setCurrentPage(1);
  };

  return (
    <div>
      <ConfirmDialog
        header="Confirm Delete"
        rejectClassName="p-button-secondary outline"
        message={
          <span>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
              <i className="pi pi-exclamation-triangle"></i>
              Are you sure you want to delete this transaction? This cannot be undone.
            </span>
            <br />
            <br />
            Deleting the transaction won't refund your customer. The transaction will simply be removed from your records.
          </span>
        }
        visible={confirmDelete}
        className="confirm-delete-popup"
        accept={() => {
          deleteRequest();
        }}
        reject={() => {
          setConfirmDelete(false);
          setSelectedRecord();
        }}
        onHide={() => {
          setConfirmDelete(false);
          setSelectedRecord();
        }}
      />
      {Boolean(showRecordModal) && (
        <EditPaymentRecordV2
          show={showRecordModal}
          onHide={() => {
            setShowRecordModal(false);
          }}
          selectedRecord={selectedRecord}
          onSuccess={onCreateSuccess}
          isLeadSpecific={isLeadSpecific}
        />
      )}
      {Boolean(showRefundModal) && (
        <RefundPaymentV2
          show={showRefundModal}
          onHide={() => setShowRefundModal(false)}
          selectedRecord={selectedRecord}
          onSuccess={onCreateSuccess}
          isLeadSpecific={isLeadSpecific}
        />
      )}
      <PaymentHistoryFiltersV2
        searchKey={searchKey}
        onChangeSearch={onChangeSearch}
        onSearchKeyDown={onSearchKeyDown}
        dateFilter={dateFilter}
        onChangeCalendar={onChangeCalendar}
        selectedPractice={selectedPractice}
        onChangePractice={onChangePractice}
        refreshData={refreshData}
        isFromCaseSpecificModule={isFromCaseSpecificModule}
        isLeadSpecific={isLeadSpecific}
      />

      <div className="datatable-responsive bg-white shadow-middle mb-3">
        <ResizableDataTable
          value={paymentRecords || []}
          className="p-datatable-responsive"
          paginator={false}
          storageKey="payment_history_column_widths"
          emptyMessage={isLoading ? 'Loading...' : 'No records found'}
          sortMode="server"
          sortField={sortField}
          sortOrder={sortOrder}
          onSort={onSort}
          removableSort={false}
          columns={[
            { field: 'date', header: 'Date', bodyClassName: 'ellipsis-text', body: dateBody, sortable: true },
            { field: 'case', header: 'Case', bodyClassName: 'ellipsis-text', body: caseBody, hidden: !isFromGlobalBilling, sortable: true },
            { field: 'related_to', header: 'Related To', bodyClassName: 'ellipsis-text', body: relatedToBody, sortable: true },
            { field: 'entered_by', header: 'Entered By', bodyClassName: 'ellipsis-text', body: enterByBody, sortable: true },
            { field: 'notes', header: 'Notes', bodyClassName: 'ellipsis-text', body: notesBody, sortable: true },
            { field: 'debit_account_name', header: 'Paid From', bodyClassName: 'ellipsis-text', body: paidFromBody, sortable: true },
            { field: 'deposit_account_name', header: 'Paid To', bodyClassName: 'ellipsis-text', body: paidToBody, sortable: true },
            { field: 'payment_method', header: 'Payment Method', bodyClassName: 'ellipsis-text', body: paymentMethodBody, sortable: true },
            { field: 'amount', header: 'Amount', bodyClassName: 'ellipsis-text', body: amountBody, sortable: true },
            {
              field: 'actions',
              header: '',
              bodyClassName: 'ellipsis-text',
              body: actionBody,
              sortable: false,
              fixed: true,
              width: '140px',
              resizable: false,
            },
          ]}
        />
        <div className="d-flex justify-content-start mt-3">
          <EllipsisPagination
            currentPage={currentPage}
            totalRecords={totalRecords}
            pageSize={pageLimit}
            onPageChange={handlePageChange}
            onPageSizeChange={handlePageSizeChange}
          />
        </div>
      </div>
    </div>
  );
};

export default PaymentHistoryV2;
